import React from 'react';
import { Alert, AlertTitle, Button, Box } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useOrganization } from '../../context/OrganizationContext';
import { useNavigate } from 'react-router-dom';

/**
 * ReadOnlyBanner Component
 *
 * Displays a prominent banner when organization subscription is inactive.
 * Shows across all organization pages to inform users of read-only status.
 *
 * Features:
 * - Displays when subscription status is not 'active'
 * - Shows clear message about data preservation
 * - Provides "Reactivate Subscription" button
 * - Sticky positioning at top of content area
 */
function ReadOnlyBanner() {
  const { subscriptionStatus, currentOrganization, userRole } = useOrganization();
  const navigate = useNavigate();
  const isOwner = userRole === 'owner';

  // Only show banner if subscription is not active
  if (subscriptionStatus === 'active' || !currentOrganization) {
    return null;
  }

  const handleReactivate = () => {
    // Navigate to billing/subscription page
    navigate('/settings/organization?tab=billing');
  };

  const getStatusMessage = () => {
    switch (subscriptionStatus) {
      case 'past_due':
        return {
          title: 'Payment Past Due',
          message: `Your subscription payment is overdue. Please update your payment method to restore full access.${isOwner ? ' Need help? Contact our support team at support@flyzone.io' : ''}`,
          severity: 'warning'
        };
      case 'canceled':
        return {
          title: 'Subscription Canceled',
          message: `Your subscription has been canceled. Your data is preserved and you can reactivate anytime to restore full access.${isOwner ? ' Need help? Contact our support team at support@flyzone.io' : ''}`,
          severity: 'error'
        };
      case 'incomplete':
        return {
          title: 'Subscription Incomplete',
          message: `Your subscription setup is incomplete. Please complete the payment process to activate your account.${isOwner ? ' Need help? Contact our support team at support@flyzone.io' : ''}`,
          severity: 'warning'
        };
      case 'trialing':
        return {
          title: 'Trial Period',
          message: 'You are currently in your trial period. Full access is available.',
          severity: 'info'
        };
      default:
        return {
          title: 'Subscription Inactive',
          message: `Your subscription is inactive. Your data is safe and preserved, but you can only view it in read-only mode.${isOwner ? ' Need help? Contact our support team at support@flyzone.io' : ''}`,
          severity: 'warning'
        };
    }
  };

  const statusInfo = getStatusMessage();

  // Don't show banner for trialing status (it's active)
  if (subscriptionStatus === 'trialing') {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        marginBottom: 2
      }}
    >
      <Alert
        severity={statusInfo.severity}
        icon={<LockIcon fontSize="large" />}
        action={
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={handleReactivate}
            sx={{
              fontWeight: 600,
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Reactivate Subscription
          </Button>
        }
        sx={{
          '& .MuiAlert-message': {
            flex: 1,
            paddingTop: 1
          },
          '& .MuiAlert-action': {
            paddingTop: 1
          }
        }}
      >
        <AlertTitle sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          {statusInfo.title}
        </AlertTitle>
        {statusInfo.message}
      </Alert>
    </Box>
  );
}

export default ReadOnlyBanner;
